// Analytics: GA4 (production only) + optional Plausible. Event tracking.

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
    plausible?: (name: string, opts?: { props: Record<string, string> }) => void;
  }
}

const GA4_ID = import.meta.env.VITE_GA4_ID as string | undefined;
const PLAUSIBLE_DOMAIN = import.meta.env.VITE_PLAUSIBLE_DOMAIN as string | undefined;
const DEPLOY_ENV = (import.meta.env.VITE_DEPLOY_ENV as string) || 'production';

const isProduction = DEPLOY_ENV === 'production';
const isLocalhost =
  typeof window !== 'undefined' &&
  /^localhost$|^127\.\d+\.\d+\.\d+$/.test(window.location?.hostname ?? '');

const analyticsReady =
  typeof window !== 'undefined' && isProduction && !isLocalhost;

function shouldLoadGA4(): boolean {
  return Boolean(analyticsReady && isProduction && GA4_ID);
}

function shouldLoadPlausible(): boolean {
  return Boolean(analyticsReady && isProduction && PLAUSIBLE_DOMAIN);
}

if (typeof window !== 'undefined') {
  window.dataLayer = window.dataLayer || [];
}

let analyticsInitialized = false;

/** @internal Reset module state between unit tests. */
export function __resetAnalyticsState(): void {
  analyticsInitialized = false;
  if (typeof window !== 'undefined') {
    window.gtag = undefined as unknown as typeof window.gtag;
    window.plausible = undefined;
  }
}

/** Call once after cookie consent. Injects GA4 and/or Plausible only in production when IDs are set. */
export function initAnalytics(): void {
  if (typeof window === 'undefined' || analyticsInitialized) return;
  if (!shouldLoadGA4() && !shouldLoadPlausible()) return;

  analyticsInitialized = true;
  window.dataLayer = window.dataLayer || [];

  if (shouldLoadGA4() && GA4_ID) {
    const gtag = (...args: unknown[]) => window.dataLayer?.push(args);
    window.gtag = gtag;
    gtag('js', new Date());
    gtag('config', GA4_ID, {
      anonymize_ip: true,
      send_page_view: false, // page_view sent manually after consent + on route change
    });

    const existing = document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${GA4_ID}"]`);
    if (!existing) {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
      document.head.appendChild(script);
    }
  }

  if (shouldLoadPlausible() && PLAUSIBLE_DOMAIN) {
    const existing = document.querySelector('script[src="https://plausible.io/js/script.js"]');
    if (!existing) {
      const script = document.createElement('script');
      script.defer = true;
      script.dataset.domain = PLAUSIBLE_DOMAIN;
      script.src = 'https://plausible.io/js/script.js';
      document.head.appendChild(script);
    }
  }
}

/** True when analytics scripts are initialized (post-consent). */
export function isAnalyticsActive(): boolean {
  if (!analyticsInitialized || typeof window === 'undefined') return false;
  return Boolean(window.gtag || window.plausible);
}

/** Single entry point for custom events. No-op until analytics is initialized (post-consent). */
export function trackEvent(eventName: string, parameters?: Record<string, unknown>): void {
  if (typeof window === 'undefined' || !isAnalyticsActive()) return;
  try {
    if (window.gtag) {
      window.gtag('event', eventName, parameters ?? {});
    }
    if (window.plausible && parameters) {
      const props: Record<string, string> = {};
      for (const [k, v] of Object.entries(parameters)) {
        if (v != null) props[k] = String(v);
      }
      window.plausible(eventName, { props });
    }
  } catch {
    // avoid breaking app if analytics fails
  }
}

// Page view tracking (called after consent + on route change)
export const trackPageView = (pagePath: string, pageTitle?: string) => {
  if (typeof window === 'undefined' || !isAnalyticsActive()) return;
  const pageLocation = `${window.location.origin}${pagePath}`;
  trackEvent('page_view', {
    page_path: pagePath,
    page_title: pageTitle,
    page_location: pageLocation,
  });
};

// Navigation tracking
export const trackNavigation = (
  linkText: string,
  linkUrl: string,
  location: 'header' | 'footer' | 'mobile-menu' | 'hero'
) => {
  trackEvent('navigation_click', {
    link_text: linkText,
    link_url: linkUrl,
    navigation_location: location,
  });
  if (linkUrl === '/contact') {
    trackEvent('contact_cta_click', { source: location });
  }
};

// Contact form tracking
export const trackFormStart = () => {
  trackEvent('form_start', {
    form_name: 'contact_form',
    form_location: 'contact_page',
  });
};

export const trackFormSubmit = (success: boolean, errorMessage?: string) => {
  const eventName = success ? 'form_submit_success' : 'form_submit_error';
  trackEvent(eventName, {
    form_name: 'contact_form',
    form_location: 'contact_page',
    ...(errorMessage && { error_message: errorMessage }),
  });
};

export const trackFormFieldInteraction = (fieldName: string) => {
  trackEvent('form_field_interaction', {
    form_name: 'contact_form',
    field_name: fieldName,
    action: 'focus',
  });
};

// Theme toggle tracking
export const trackThemeToggle = (newTheme: 'light' | 'dark') => {
  trackEvent('theme_toggle', { new_theme: newTheme });
};

// Language toggle tracking
export const trackLanguageToggle = (newLanguage: string) => {
  trackEvent('language_toggle', { new_language: newLanguage });
};

// Project card click (single event)
export const trackProjectView = (projectId: string, projectTitle: string) => {
  trackEvent('project_card_click', { project_id: projectId, project_title: projectTitle });
};

// External link tracking
export const trackExternalLink = (linkText: string, linkUrl: string, location: string) => {
  trackEvent('external_link_click', {
    link_text: linkText,
    link_url: linkUrl,
    location: location,
  });
};

// =============================================================================
// Health Check Analytics
// =============================================================================

/** Track when user starts the health check wizard */
export const trackHealthCheckStart = (source: string) => {
  trackEvent('health_check_start', {
    source, // e.g., 'legacy_rescue_page', 'direct', 'hero_cta'
  });
};

/** Track step progression in the health check wizard */
export const trackHealthCheckStep = (
  step: 'connect' | 'context' | 'analyzing' | 'results',
  connectMethod?: 'github' | 'zip' | 'cli'
) => {
  trackEvent('health_check_step', {
    step,
    ...(connectMethod && { connect_method: connectMethod }),
  });
};

/** Track health check completion with key metrics */
export const trackHealthCheckComplete = (params: {
  score: number;
  riskLabel: string;
  hasToken: boolean;
  connectMethod: 'github' | 'zip' | 'cli';
  framework?: string;
  isPrivateRepo?: boolean;
}) => {
  trackEvent('health_check_complete', {
    score: params.score,
    risk_label: params.riskLabel,
    has_token: params.hasToken ? 'yes' : 'no',
    connect_method: params.connectMethod,
    framework: params.framework || 'unknown',
    repo_type: params.isPrivateRepo ? 'private' : 'public',
  });
};

/** Track health check errors */
export const trackHealthCheckError = (errorType: string, errorMessage: string) => {
  trackEvent('health_check_error', {
    error_type: errorType,
    error_message: errorMessage,
  });
};

/** Track when user requests results via email */
export const trackHealthCheckEmailRequest = (score: number) => {
  trackEvent('health_check_email_request', {
    score,
  });
};

/** Track booking request from health check results */
export const trackHealthCheckBooking = (score: number) => {
  trackEvent('health_check_booking_request', {
    score,
  });
};
